<template>
  <AuthLayout :mode="mode">
    <Section heading="Manage Document Library" :mode="mode" />
    <AppCard :mode="mode">
      <template #body>
        <div class="grid gap-6">
          <div
            class="rounded-lg border p-4"
            :class="[
              mode === 'dark' ? 'border-dark-border' : 'border-border-10',
            ]"
          >
            <h3 class="mb-3 text-[18px] font-semibold">Upload a document</h3>
            <div class="grid grid-cols-3 gap-3 sm:grid-cols-1">
              <div>
                <label class="mb-1 block font-semibold">File</label>
                <input
                  type="file"
                  ref="fileInput"
                  @change="onFileChosen"
                  class="w-full text-[14px]"
                />
              </div>
              <AppInput
                :mode="mode"
                id="upload-name"
                title="Stored as (optional override)"
                placeholder="Leave blank to use the file's name"
                v-model="overrideName"
              />
              <div class="flex items-end">
                <AppButton
                  title="Upload"
                  @action="upload"
                  :isDisabled="!selectedFile"
                />
              </div>
            </div>
            <p v-if="selectedFile" class="mt-2 text-[12px] text-black-60">
              Selected: <strong>{{ selectedFile.name }}</strong>
              ({{ formatSize(selectedFile.size) }})
            </p>
          </div>

          <div
            class="overflow-hidden rounded-xl border"
            :class="[
              mode === 'dark'
                ? 'border-dark-border bg-dark-sidebar'
                : 'border-border-10 bg-white-100',
            ]"
          >
            <div class="without-scrollbar overflow-x-auto">
              <table
                class="table"
                :class="[mode === 'dark' ? 'dark-table' : '']"
              >
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>Size</th>
                    <th class="w-[140px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(file, key) in files" :key="key">
                    <td>{{ file.name }}</td>
                    <td>{{ formatSize(file.size) }}</td>
                    <td>
                      <div class="flex items-center gap-2">
                        <a
                          :href="file.url"
                          target="_blank"
                          class="text-primary-100 underline"
                          >View</a
                        >
                        <button
                          class="text-red-600 underline"
                          @click="confirmDelete(file)"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="files.length === 0">
                    <td colspan="3" class="p-4 text-center text-black-60">
                      No documents.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </AppCard>
  </AuthLayout>
</template>

<script>
import ApiRequest from "@L/ApiRequest";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import Section from "@C/Common/Section.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppButton from "@C/Common/AppButton.vue";
import AppInput from "@C/Form/AppInput.vue";
import AuthLayout from "@A/AuthLayout.vue";

export default {
  name: "AdminLibrary",
  components: { AuthLayout, Section, AppCard, AppButton, AppInput },
  setup() {
    return {
      toastStore: useToastStore(),
      loaderStore: useLoaderStore(),
    };
  },
  props: { mode: String },
  data() {
    return {
      files: [],
      selectedFile: null,
      overrideName: "",
    };
  },
  mounted() {
    this.fetch();
  },
  methods: {
    formatSize(n) {
      if (n == null) return "";
      const v = Number(n);
      if (Number.isNaN(v)) return n;
      if (v < 1024) return `${v} B`;
      if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
      return `${(v / 1024 / 1024).toFixed(2)} MB`;
    },
    async fetch() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/admin/library_files`);
        this.files = data || [];
      } catch (e) {
        this.toastStore.show("Failed to load documents", "error");
        this.files = [];
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    onFileChosen(ev) {
      this.selectedFile = (ev.target.files && ev.target.files[0]) || null;
    },
    async upload() {
      if (!this.selectedFile) return;
      const form = new FormData();
      form.append("file", this.selectedFile);
      if (this.overrideName.trim()) {
        form.append("name", this.overrideName.trim());
      }
      this.loaderStore.setIsLoading(true);
      try {
        await ApiRequest().post(`/admin/library_files`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.toastStore.show("Uploaded", "info");
        this.selectedFile = null;
        this.overrideName = "";
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
        this.fetch();
      } catch (e) {
        this.toastStore.show(
          (e.response && e.response.data && e.response.data.error) ||
            "Upload failed",
          "error",
        );
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async confirmDelete(file) {
      if (!confirm(`Delete "${file.name}"?`)) return;
      this.loaderStore.setIsLoading(true);
      try {
        await ApiRequest().delete(
          `/admin/library_files/${encodeURIComponent(file.name)}`,
        );
        this.toastStore.show("Deleted", "info");
        this.fetch();
      } catch (e) {
        this.toastStore.show("Delete failed", "error");
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
  },
};
</script>
